// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { WifiOff, RefreshCw } from 'lucide-react';
import Banner from '../Banner';
import { useOfflineSync } from '../../offline/connectivity';
import { discardOutboxItem } from '../../offline/outbox';

// Mounted once in AppLayout (#22) — visible across every /apps/* page.
// onEditRetry is optional: the page that actually owns the transaction
// form (Transactions.jsx) wires it to reopen that form pre-filled from a
// failed item's stored payload. Without it, "Edit and retry" is hidden and
// "Discard" still works on its own.
export default function OfflineBanner({ onEditRetry }) {
  const { isOnline, pendingCount, failedItems, syncNow, refresh } = useOfflineSync();

  if (isOnline && pendingCount === 0 && failedItems.length === 0) return null;

  return (
    <div className="space-y-2 mb-4">
      {!isOnline && (
        <Banner type="warning" message="You're offline — changes will sync when you're back online." />
      )}

      {pendingCount > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border bg-blue-50 border-blue-200 text-blue-800 text-sm">
          {!isOnline ? <WifiOff size={16} className="shrink-0" /> : <RefreshCw size={16} className="shrink-0" />}
          <span className="flex-1">
            {pendingCount} change{pendingCount === 1 ? '' : 's'} waiting to sync.
          </span>
          {isOnline && (
            <button
              onClick={syncNow}
              className="text-xs font-medium underline hover:no-underline"
            >
              Sync now
            </button>
          )}
        </div>
      )}

      {failedItems.length > 0 && (
        <div className="rounded-xl border bg-red-50 border-red-200 text-red-800 text-sm">
          <div className="px-4 py-2 font-medium border-b border-red-200">
            {failedItems.length} change{failedItems.length === 1 ? '' : 's'} couldn't sync
          </div>
          <ul className="divide-y divide-red-200">
            {failedItems.map((item) => (
              <li key={item.localId} className="flex items-center gap-3 px-4 py-2">
                <span className="flex-1">{item.error || 'Sync failed.'}</span>
                {onEditRetry && (
                  <button
                    onClick={() => onEditRetry(item)}
                    className="text-xs font-medium underline hover:no-underline"
                  >
                    Edit and retry
                  </button>
                )}
                <button
                  onClick={async () => {
                    await discardOutboxItem(item.localId);
                    await refresh();
                  }}
                  className="text-xs font-medium underline hover:no-underline"
                >
                  Discard
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
